// docs/29 — the encounter modifiers in the HUD, the target preview and playback. The rules and
// their words are the sim's (`fk_sim::modifiers`, `ModifierNote`); this file only gives each a
// glyph and puts a sway's number beside it. Nothing here decides whether a modifier holds.
import type { ModifierNote, SwayNote, Tile } from './types';

/** One glyph per modifier (the `Modifier` variant name), drawn in the HUD, on the board's edges and over a hit. */
export const MODIFIER_GLYPH: Record<string, string> = {
  LowSun: '☀', Wind: '≋', HeatSpent: '☼', NumbCold: '❅', Unseen: '◐', HarvestMoon: '☽',
  SnowGlare: '✧', Mirage: '∿', Drenched: '☂', Furnace: '♨', SpringSap: '❀', WinterBite: '❆',
  HighGround: '▲',
};

/** A few words for a sway when the survey's notes are not at hand (playback of an old pulse). */
const SWAY_WORDS: Record<string, string> = {
  LowSun: 'into the sun', Wind: 'the wind', HeatSpent: 'heat-spent', NumbCold: 'numb with cold', Unseen: 'unseen shot',
  HarvestMoon: 'harvest moon', SnowGlare: 'snow glare', Mirage: 'mirage', Drenched: 'wet through', Furnace: 'furnace air',
  SpringSap: "spring's vigour", WinterBite: "winter's bite", HighGround: 'high ground',
};

export const glyphOf = (modifier: string): string => MODIFIER_GLYPH[modifier] ?? '✦';

/** `−1`, `+1` with a real minus sign. */
export function signed(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0';
}

/** The arrow for a side: east `→`, west `←`. */
export const arrow = (toward: number): string => (toward > 0 ? '→' : toward < 0 ? '←' : '');

/** What playback floats over the unit after the number it changed: `☀ −1`, `☂ −1 turn`. */
export function swayTag(modifier: string, delta: number): string {
  const unit = modifier === 'Drenched' || modifier === 'Furnace' ? ` turn${Math.abs(delta) === 1 ? '' : 's'}` : '';
  return `${glyphOf(modifier)} ${signed(delta)}${unit}`;
}

/** The preview's line for one sway: `☀ −1 · Sun in the east` (the note's name when we have it). */
export function swayLine(s: Pick<SwayNote, 'modifier' | 'delta'>, notes: readonly ModifierNote[] = []): string {
  const name = notes.find((n) => n.modifier === s.modifier)?.name ?? SWAY_WORDS[s.modifier] ?? s.modifier;
  return `${swayTag(s.modifier, s.delta)} · ${name}`;
}

/** Which way `to` lies from `from` on the board's x axis, as the sim's `lean` reads one-square bodies. */
export function leanOf(from: Tile, to: Tile): -1 | 0 | 1 {
  return to.x > from.x ? 1 : to.x < from.x ? -1 : 0;
}

/**
 * Squares the wind adds to a missile from `from` at `to` (the survey's `windShift`, docs/29 X2),
 * for the preview's "range 6, +1 with the wind". Zero when the wind does not touch it.
 */
export function windFor(shift: readonly [number, number, number] | undefined, from: Tile, to: Tile): number {
  if (!shift) return 0;
  return shift[leanOf(from, to) + 1] ?? 0;
}

/** A stable key for "these modifiers are in force" (the HUD shows the card once per set). */
export function modifiersKey(notes: readonly ModifierNote[]): string {
  return notes.map((n) => `${n.key}${n.toward ? `:${n.toward}` : ''}`).join(',');
}
